import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { FormControl, FormHelperText, ListSubheader, MenuItem, Select } from "@mui/material"
import { LuChevronDown } from "react-icons/lu"
import { DropdownSearchContext } from "./DropdownSearchContext"
import DropdownSearchField from "./DropdownSearchField"
import DropdownSearchOption from "./DropdownSearchOption"
import DropdownSearchPaper from "./DropdownSearchPaper"
import {
  SEARCH_AFTER,
  SEARCH_LIST_MAX_HEIGHT,
  SEARCH_ROW_HEIGHT,
  createHighlight,
  highlightTarget,
  isDisabledOption,
  isSameValue,
  lockMenuSize,
  nextSearchListId,
  normalizeQuery,
  optionDomId,
  optionKey,
  scrollToLastChosen,
  scrollToOption,
  unlockMenuSize,
  visibleOptions,
} from "./dropdownSearch"

function MyListSubheader(props) {
  return <ListSubheader {...props} />
}

MyListSubheader.muiSkipListHighlight = true

const MENU_MAX_HEIGHT = 190
const MENU_MARGIN = 16
const SEARCH_MENU_MAX_HEIGHT = SEARCH_ROW_HEIGHT + SEARCH_LIST_MAX_HEIGHT
const MENU_PAPER_MAX_HEIGHT = 'calc(100% - 96px)'
const OPEN_ABOVE = {
  anchorOrigin: { vertical: 'top', horizontal: 'center' },
  transformOrigin: { vertical: 'bottom', horizontal: 'center' },
}

const placeMenu = (field, maxHeight) => {
  const { top, bottom } = field.getBoundingClientRect()
  const roomBelow = window.innerHeight - bottom
  return { width: field.clientWidth, above: roomBelow < maxHeight + MENU_MARGIN && top > roomBelow }
}

const Dropdown = ({
  small = false,
  medium = false,
  myListSubheader,
  helperText,
  label,
  value,
  className,
  menuItems,
  setValue,
  // MUI suppresses onChange when the clicked option already is the value, so a dropdown that
  // shows an uncommitted default has no way to commit it. MenuItem's own onClick still fires.
  onItemClick,
  setElement,
  placeholder,
  fontSizeDropdown,
  fontStyleDropdown,
  height,
  heightPaddings,
  isMulti = false,
  disabled = false,
  errorMsg,
  // Several dropdowns render with no visible label (filter bars, table cells), which
  // leaves an unnamed combobox for screen readers and for role-based queries in tests.
  ariaLabel,
  menuFitsField = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPlace, setMenuPlace] = useState(null)
  const fieldRef = useRef(null)
  const hasError = Boolean(errorMsg)
  const searchAllowed = useContext(DropdownSearchContext)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight] = useState(createHighlight)
  const [listId] = useState(nextSearchListId)
  const listRef = useRef(null)
  const sizeLocked = useRef(false)
  const searchInputRef = useRef(null)

  const handleChange = (event) => {
    setElement ? menuItems?.map((e) => {
      if (e?.value === event.target.value) setValue(e)
    }) :
      setValue(event.target.value)

  }
  const styleSmall = small && {
    padding: '0 8px 0 12px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '36px',
    boxSizing: 'border-box',
  }
  const styleMedium = medium && {
    padding: '0 8px 0 12px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
    boxSizing: 'border-box',
  }
  const styleCustom = heightPaddings && {
    position: 'relative',
    bottom: 8
  }
  const allMenuItems = Object.values(menuItems || {}).flat()
  const needle = normalizeQuery(query)
  const searchEntries = searchOpen ? visibleOptions({ menuItems, myListSubheader, needle }) : []
  const visibleKeys = new Set(searchEntries.map(({ key }) => key))
  const pickableKeys = searchEntries.filter(({ pickable }) => pickable).map(({ key }) => key)
  const pickableSet = new Set(pickableKeys)
  const currentKey = isMulti ? undefined : searchEntries.find(({ item, pickable }) => pickable && isSameValue(item?.value, value))?.key
  const fallbackKey = (needle ? pickableKeys[0] : currentKey) ?? null
  const activeOf = (navigatedKey) => (pickableSet.has(navigatedKey) ? navigatedKey : fallbackKey)
  const noMatches = searchOpen && needle !== '' && searchEntries.length === 0
  const listName = ariaLabel || (typeof label === 'string' ? label : '')
  const domId = (key) => optionDomId(listId, key)
  const OptionItem = searchOpen ? DropdownSearchOption : MenuItem
  const searchOptionProps = (key) => ({ id: domId(key), optionKey: key, highlight, activeOf })

  const attachList = useCallback((node) => {
    listRef.current = node
    if (node) {
      scrollToLastChosen(node)
      return
    }
    sizeLocked.current = false
    setSearchOpen(false)
    setQuery('')
    highlight.set(null)
  }, [highlight])

  const handleSearchChange = (event) => {
    const paper = listRef.current?.parentElement
    if (!sizeLocked.current && paper && fieldRef.current) {
      sizeLocked.current = true
      lockMenuSize(paper, fieldRef.current, { width: !menuFitsField })
    }
    setQuery(event.target.value)
    highlight.set(null)
  }

  const handleSearchKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return
    const activeKey = activeOf(highlight.get())
    const targetKey = highlightTarget(event.key, pickableKeys, activeKey, needle === '')
    if (targetKey !== undefined) {
      event.preventDefault()
      event.stopPropagation()
      highlight.set(targetKey)
      scrollToOption(listRef.current, activeOf(highlight.get()))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      if (activeKey) document.getElementById(domId(activeKey))?.click()
    } else if (event.key === 'Tab') {
      event.preventDefault()
      event.stopPropagation()
      setIsOpen(false)
    }
  }

  const keepFocusInSearch = (event) => {
    if (event.target !== searchInputRef.current) event.preventDefault()
  }

  useEffect(() => {
    if (isOpen && searchOpen) searchInputRef.current?.focus()
  }, [isOpen, searchOpen])

  useEffect(() => {
    if (isOpen && searchOpen) scrollToOption(listRef.current, fallbackKey)
  }, [isOpen, searchOpen, fallbackKey, needle])

  return (
    <div className={`flex items-center justify-center ${className}`}>

      <FormControl fullWidth>
        {/* Rendered unconditionally this div still reserved its mb-2, pushing every
            label-less dropdown ~4px below the other cell content in table rows. */}
        {label && (
          <div className={`flex justify-start flex-col mb-2 text-[13px] leading-5 text-start max-w-max ${hasError ? "text-primary-static-error-600" : "text-text-secondary"}`}>
            {label}
          </div>
        )}

        <Select
          ref={fieldRef}
          disabled={disabled}
          multiple={isMulti}
          renderValue={(selected) => {
            if (!isMulti || !Array.isArray(selected)) {
              const single = allMenuItems.find(i => i.value === selected)
              return single?.label || ''
            }

            // Compact display for multiselect: "First item +N more"
            if (selected.length === 0) {
              return ''
            }

            const firstLabel = allMenuItems.find(i => i.value === selected[0])?.label || selected[0]

            if (selected.length === 1) {
              return firstLabel
            }

            return `${firstLabel} +${selected.length - 1} more`
          }}
          IconComponent={() => (
            <div className="pr-2 text-text-secondary">
              <LuChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          )}
          // MUI fixes controlled-vs-uncontrolled on the FIRST render, and undefined
          // reads as uncontrolled. Callers that derive the value from state that starts
          // empty — FiltersInputLine takes `selectedFilter?.value` from useState({}) —
          // therefore mount uncontrolled and flip on the first pick. That flip threw out
          // of SelectInput with no error boundary above it, so choosing a filter
          // unmounted the app and left a blank page. Always hand Select a defined value.
          //
          // This is the contract, and it lives here rather than as a `value = ''` default
          // on the prop: the empty value differs by variant, and a signature default
          // cannot see isMulti — `''` would reach a multiple Select, which MUI rejects
          // outright ("the value prop must be an array").
          value={value ?? (isMulti ? [] : '')}
          label={label}
          inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
          onChange={handleChange}
          open={isOpen}
          onOpen={() => {
            const searchable = searchAllowed && allMenuItems.length > SEARCH_AFTER
            if (menuFitsField && fieldRef.current) setMenuPlace(placeMenu(fieldRef.current, searchable ? SEARCH_MENU_MAX_HEIGHT : MENU_MAX_HEIGHT))
            setSearchOpen(searchable)
            setQuery('')
            highlight.set(null)
            sizeLocked.current = false
            if (listRef.current?.parentElement) unlockMenuSize(listRef.current.parentElement)
            setIsOpen(true)
          }}
          onClose={() => setIsOpen(false)}
          MenuProps={searchOpen ? {
            ...(menuPlace?.above && OPEN_ABOVE),
            autoFocus: false,
            slots: { paper: DropdownSearchPaper },
            PaperProps: {
              onMouseDown: keepFocusInSearch,
              search: (
                <>
                  <DropdownSearchField
                    ref={searchInputRef}
                    highlight={highlight}
                    activeOf={activeOf}
                    listId={listId}
                    aria-label={`Search ${listName}`.trim()}
                    value={query}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                  />
                  <div role="status" className={noMatches ? "px-4 py-1.5 text-lg text-text-secondary" : "sr-only"}>
                    {noMatches ? 'No matches' : ''}
                  </div>
                </>
              ),
              sx: {
                maxHeight: MENU_PAPER_MAX_HEIGHT,
                ...(menuPlace?.width && { width: menuPlace.width }),
                bgcolor: 'var(--color-bg-paper)',
                border: '1px solid var(--color-divider)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            },
            MenuListProps: {
              ref: attachList,
              id: listId,
              'aria-label': listName || undefined,
              sx: {
                maxHeight: SEARCH_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                flex: '0 1 auto',
                minHeight: 0,
                ...(noMatches && { display: 'none' }),
              }
            }
          } : {
            ...(menuPlace?.above && OPEN_ABOVE),
            PaperProps: {
              sx: {
                maxHeight: MENU_MAX_HEIGHT,
                ...(menuPlace?.width && { width: menuPlace.width }),
                bgcolor: 'var(--color-bg-paper)',
                border: '1px solid var(--color-divider)',
                '& ul': {
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': { display: 'none' }
                }
              }
            }
          }}
          sx={{
            '.MuiSelect-outlined.MuiInputBase-input': {
              ...styleSmall,
              ...styleMedium,
              ...styleCustom
            },
            '.MuiOutlinedInput-input': {
              padding: isMulti ? '0px 14px !important' : ''
            },
            'div': height ? heightPaddings : small || medium ? {} : {
              minHeight: '56px',
              display: 'flex',
              alignItems: 'center',
              paddingTop: 0,
              paddingBottom: 0,
              boxSizing: 'border-box',
            },
            'fieldset': {
              top: 0,
              border: '1px solid',
              height: height ? height : small ? '36px' : medium ? '40px' : '56px',
              borderRadius: '8px',
              borderColor: hasError ? '#F04438' : 'var(--color-divider)',
              '--tw-border-opacity': '1',
              'legend': {
                display: 'none'
              }
            },
            '.pr-2': {
              pointerEvents: 'none'
            },
            '& .MuiSelect-select .notranslate::after': placeholder
              ? {
                content: `"${placeholder}"`,
                opacity: 0.42,
              }
              : {},
            '& p': {
              fontSize: fontSizeDropdown,
              fontWeight: fontStyleDropdown,
            },
            '&:hover fieldset': {
              borderColor: hasError ? '#F04438 !important' : 'var(--color-primary-main) !important', // primary.main, using var to ensure precedence or theme color
            },
            '&.Mui-focused fieldset': {
              borderColor: hasError ? '#F04438 !important' : 'var(--color-primary-main) !important',
              borderWidth: '1px !important',
            }
          }}
        >
          {myListSubheader?.length > 0 && myListSubheader.flatMap((item, index) => [
            (searchOpen && needle
              ? menuItems[item?.value]?.some((menuItem, itemIndex) => visibleKeys.has(optionKey(index, itemIndex)))
              : menuItems[item?.value]?.length !== 0) &&
            <MyListSubheader key={`header-${index}`}>{item.name}</MyListSubheader>,
            item?.value && menuItems[item?.value]?.map((menuItem, itemIndex) => (searchOpen && !visibleKeys.has(optionKey(index, itemIndex)) ? null :
              <OptionItem value={menuItem?.value} className="text-left w-full hover:bg-primary-main/5 text-text-secondary"
                {...(searchOpen && searchOptionProps(optionKey(index, itemIndex)))}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'color-mix(in srgb, var(--color-primary-main) 10%, transparent) !important',
                    color: 'var(--color-text-primary)',
                    '&:hover': {
                      backgroundColor: 'color-mix(in srgb, var(--color-primary-main) 16%, transparent) !important',
                    }
                  }
                }}
                key={`item-${menuItem?.value}`}>
                {menuItem?.label}
              </OptionItem>
            ))
          ])}


          {myListSubheader === undefined && menuItems?.map((e, i) => {
            if (searchOpen && !visibleKeys.has(optionKey(null, i))) return null
            return <OptionItem key={i} value={e?.value} className="text-left" disabled={isDisabledOption(e)}
              onClick={onItemClick ? () => onItemClick(e?.value) : undefined}
              title={menuFitsField ? e?.label : undefined}
              {...(searchOpen && searchOptionProps(optionKey(null, i)))}
              children={<p className={menuFitsField ? "w-full truncate" : "w-full"}>{e?.label}</p>} />
          })}
        </Select>
        {helperText &&
          <FormHelperText><span className="text-text-secondary text-sm">{helperText}</span></FormHelperText>}
        {hasError && <p className="text-primary-static-error-600 text-sm mt-1">{errorMsg}</p>}

      </FormControl>

    </div>
  )
}

export default Dropdown